import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

// Every Portfolio's File Requests, newest first, each with the Company it is
// linked to and its status as the customer sees it.
export const loadFileRequests = createServerFn({ method: "GET" }).handler(
  async () => {
    const [{ assertAdminContext }, { readFileRequests }] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/db/queries/terminal-file-requests"),
    ])
    await assertAdminContext()
    return readFileRequests()
  }
)

export type FileRequestRow = Awaited<
  ReturnType<typeof loadFileRequests>
>[number]

const clarificationInput = z
  .object({ requestId: z.string().uuid(), isNeeded: z.boolean() })
  .strict()

// Marks a request Needs clarification, recording the signed-in admin, or
// clears the mark so it reads In progress again. The buyer sees the status
// alone; the conversation happens outside the app (Dhanur, 25 Oct). Dynamic
// imports, so the client bundle never loads the server-only modules.
export const setFileRequestClarification = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => clarificationInput.parse(data))
  .handler(async ({ data: { requestId, isNeeded } }) => {
    const [{ assertAdminActor }, { db }, { terminalFileRequest }, { eq, sql }] =
      await Promise.all([
        import("@/lib/supabase/auth"),
        import("@/lib/admin-db"),
        import("@/db/schema/terminal-portfolio"),
        import("drizzle-orm"),
      ])
    const actor = await assertAdminActor()
    const [updated] = await db
      .update(terminalFileRequest)
      .set(
        isNeeded
          ? { clarification_set_at: sql`now()`, clarification_set_by: actor }
          : { clarification_set_at: null, clarification_set_by: null }
      )
      .where(eq(terminalFileRequest.id, requestId))
      .returning({ id: terminalFileRequest.id })
    if (!updated) throw new Error("No such File Request")
  })

const linkInput = z
  .object({ requestId: z.string().uuid(), companyId: z.string().uuid() })
  .strict()

// Stores a Company on a request with no link yet, confirming the Company it
// matched on read, so the buyer sees it Published once that Company has a
// File there. The Company must be in the request's Portfolio.
export const linkFileRequestCompany = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => linkInput.parse(data))
  .handler(async ({ data: { requestId, companyId } }) => {
    const [
      { assertAdminContext },
      { db },
      { isCurrentPortfolioEntry, terminalFileRequest, terminalPortfolioEntry },
      { and, eq, exists, isNull },
    ] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/lib/admin-db"),
      import("@/db/schema/terminal-portfolio"),
      import("drizzle-orm"),
    ])
    await assertAdminContext()
    const [updated] = await db
      .update(terminalFileRequest)
      .set({ company_id: companyId })
      .where(
        and(
          eq(terminalFileRequest.id, requestId),
          isNull(terminalFileRequest.company_id),
          exists(
            db
              .select({ id: terminalPortfolioEntry.id })
              .from(terminalPortfolioEntry)
              .where(
                and(
                  eq(
                    terminalPortfolioEntry.portfolio_id,
                    terminalFileRequest.portfolio_id
                  ),
                  eq(terminalPortfolioEntry.company_id, companyId),
                  isCurrentPortfolioEntry
                )
              )
          )
        )
      )
      .returning({ id: terminalFileRequest.id })
    if (!updated)
      throw new Error(
        "No unlinked File Request with that Company in its Portfolio"
      )
  })
